import React from 'react';
import { Service, Addon } from '../types/booking.types';
import ReferenceImagesRow from './ReferenceImagesRow';

interface ServiceItemProps {
  service: Service;
  index: number;
  onCancelService?: (
    serviceOrServices: Service | Service[],
    meta?: {
      isFullCancellation?: boolean;
      selectedCount?: number;
      totalCancellableCount?: number;
    }
  ) => void;
  serviceKey?: string;
  checkedService?: boolean;
  onServiceCheckChange?: (checked: boolean) => void;
  showInlineCancel?: boolean;
}

const ServiceItem: React.FC<ServiceItemProps> = ({
  service,
  index,
  onCancelService,
  serviceKey,
  checkedService = false,
  onServiceCheckChange,
  showInlineCancel = true,
}) => {
  const addons = Array.isArray(service.addons) ? service.addons : [];
  const isAdditional = service.is_additional === '1' || service.is_additional === true;
  const isCancelled = service.status === 'cancelled';

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const toNumber = (value: number | string | null | undefined) => {
    const parsed = parseFloat(String(value ?? 0));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const serviceAmount =
    service.amount !== undefined && service.amount !== null
      ? toNumber(service.amount)
      : toNumber(service.rate) * toNumber(service.quantity);

  return (
    <div className={`relative mb-2 overflow-hidden rounded-md border bg-white shadow-sm ${isCancelled ? 'border-red-300 opacity-60' : 'border-gray-200'}`}>
      <div
        className={`bg-gradient-to-r px-3 py-2 ${
          isCancelled
            ? 'from-red-50 to-red-50'
            : isAdditional
            ? 'from-orange-50 to-amber-50'
            : 'from-indigo-50 to-blue-50'
        } ${addons.length > 0 ? 'border-b border-gray-200' : ''}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {serviceKey && (
              <input
                type="checkbox"
                checked={checkedService}
                onChange={(e) => onServiceCheckChange?.(e.target.checked)}
                className="h-4 w-4"
              />
            )}
            <span className="flex-shrink-0 text-xs font-bold text-gray-500">{index + 1}.</span>
            <h4 className={`flex-1 truncate text-sm font-semibold ${isCancelled ? 'text-red-600 line-through' : 'text-gray-800'}`}>
              {service.service_name}
            </h4>
            {isAdditional && (
              <span className="flex-shrink-0 rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm">
                +Additional
              </span>
            )}
            {service.status && (
              <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${getStatusColor(service.status)}`}>
                {service.status.toUpperCase()}
              </span>
            )}
          </div>

          <div className="flex flex-shrink-0 items-center gap-2 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Qty:</span>
              <span className="font-semibold text-gray-700">
                {service.quantity} {service.unit}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">@Rs {service.rate}</span>
            </div>
            {service.room_length && service.room_width && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">
                  {service.room_length}x{service.room_width}
                </span>
              </div>
            )}
            <div className="flex items-center gap-1 rounded bg-white px-2 py-1">
              <span className="font-bold text-indigo-700">Rs {parseFloat(String(serviceAmount)).toFixed(2)}</span>
            </div>
            {showInlineCancel && !isCancelled && service.status !== 'completed' && (
              <button
                type="button"
                onClick={() => onCancelService?.(service)}
                className="ml-2 rounded bg-red-500 px-2 py-1 text-[10px] font-semibold text-white hover:bg-red-600"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {service.reference_image && (
          <div className="mt-1">
            <ReferenceImagesRow referenceImage={service.reference_image} />
          </div>
        )}
      </div>

      {addons.length > 0 && (
        <div className="bg-gray-50 px-3 py-2">
          <div className="space-y-1">
            {addons.map((addon: Addon) => (
              <div key={addon.id} className="flex items-center justify-between rounded border border-gray-200 bg-white px-2 py-1.5 text-xs">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="text-gray-400">+</span>
                  <span className="truncate font-medium text-gray-700">{addon.addon_name}</span>
                  {addon.addon_description && (
                    <span className="hidden truncate text-[10px] text-gray-400 sm:inline">({addon.addon_description})</span>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-2 text-xs">
                  <span className="text-gray-500">
                    {addon.quantity} {addon.unit}
                  </span>
                  <span className="text-gray-500">@Rs {addon.rate}</span>
                  <span className="min-w-[70px] text-right font-bold text-blue-700">
                    Rs {(
                      addon.amount !== undefined && addon.amount !== null
                        ? toNumber(addon.amount)
                        : toNumber(addon.rate) * toNumber(addon.quantity)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceItem;
